import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { reportsAPI } from "../services/api";

export default function Reports() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [dashboardStats, setDashboardStats] = useState(null);
  
  // Statement filters
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  
  // Monthly report year
  const [year, setYear] = useState(new Date().getFullYear());

  // Safe number formatter for display
  const formatNumber = (num) => {
    if (num === null || num === undefined || isNaN(num)) return "0.00";
    return Number(num).toLocaleString("en-NG", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  // Plain number for CSV (no currency symbol, no commas)
  const formatNumberForCSV = (num) => {
    if (num === null || num === undefined || isNaN(num)) return "0.00";
    return Number(num).toFixed(2);
  };

  // Fetch dashboard stats
  const fetchDashboard = async () => {
    try {
      setLoading(true);
      setError("");
      const data = await reportsAPI.getDashboard();
      console.log("Dashboard data received:", data);
      setDashboardStats(data);
      alert("Statistics loaded successfully!");
    } catch (err) {
      const errorMsg = err.response?.data?.message || "Failed to load dashboard statistics";
      setError(errorMsg);
      console.error("Dashboard error:", err);
      alert("Error: " + errorMsg);
    } finally {
      setLoading(false);
    }
  };

  // Download account statement
  const downloadStatement = async () => {
    if (!startDate || !endDate) {
      setError("Please select both start and end dates");
      alert("Please select both start and end dates");
      return;
    }

    try {
      setLoading(true);
      setError("");
      
      console.log("Downloading statement for:", startDate, "to", endDate);
      
      const data = await reportsAPI.getAccountStatement(startDate, endDate);
      
      console.log("Statement data received:", data);
      
      // Calculate totals from transactions if not provided
      let totalCredit = 0;
      let totalDebit = 0;
      
      if (data.transactions && Array.isArray(data.transactions)) {
        data.transactions.forEach(t => {
          const amount = Number(t.amount) || 0;
          if (amount > 0) {
            totalCredit += amount;
          } else {
            totalDebit += Math.abs(amount);
          }
        });
      }
      
      // Use calculated values if API values are missing or zero
      const finalTotalCredit = data.totalCredit || totalCredit;
      const finalTotalDebit = data.totalDebit || totalDebit;
      
      // Helper to strip currency symbols and get plain number
      const toPlainNumber = (val) => {
        if (val === null || val === undefined) return 0;
        // Remove any currency symbols, commas, spaces
        const cleaned = String(val).replace(/[₦NGN,\s]/gi, '').trim();
        const num = parseFloat(cleaned);
        return isNaN(num) ? 0 : num;
      };

      // Create CSV content with proper number formatting for Excel
      let csv = "Account Statement\n\n";
      csv += `Period,${startDate} to ${endDate}\n`;
      csv += `Account Number,${data.accountNumber || "N/A"}\n`;
      csv += `Account Name,${data.accountName || data.fullName || "N/A"}\n`;
      csv += `Opening Balance,${toPlainNumber(data.openingBalance).toFixed(2)}\n`;
      csv += `Closing Balance,${toPlainNumber(data.closingBalance || data.currentBalance).toFixed(2)}\n`;
      csv += `Total Credit,${toPlainNumber(finalTotalCredit).toFixed(2)}\n`;
      csv += `Total Debit,${toPlainNumber(finalTotalDebit).toFixed(2)}\n\n`;
      
      csv += "Date,Type,Description,Amount,Balance After,Status\n";
      
      if (data.transactions && Array.isArray(data.transactions) && data.transactions.length > 0) {
        data.transactions.forEach(t => {
          const date = t.createdAt ? new Date(t.createdAt).toLocaleString() : "N/A";
          // Remove currency symbols from description too
          const description = (t.description || "").replace(/[₦]/g, 'N').replace(/"/g, '""');
          const amount = toPlainNumber(t.amount).toFixed(2);
          const balance = toPlainNumber(t.balanceAfter).toFixed(2);
          csv += `"${date}","${t.type || "N/A"}","${description}",${amount},${balance},"${t.status || "Completed"}"\n`;
        });
      } else {
        csv += "No transactions found for this period\n";
      }
      
      // Download CSV with BOM for Excel compatibility
      const BOM = '\uFEFF';
      const blob = new Blob([BOM + csv], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `statement_${startDate}_to_${endDate}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      
      alert("Statement downloaded successfully!");
    } catch (err) {
      const errorMsg = err.response?.data?.message || err.message || "Failed to download statement";
      setError(errorMsg);
      console.error("Statement download error:", err);
      console.error("Error response:", err.response?.data);
      alert("Error: " + errorMsg);
    } finally {
      setLoading(false);
    }
  };

  // Download monthly report
  const downloadMonthlyReport = async () => {
    try {
      setLoading(true);
      setError("");
      
      console.log("Downloading monthly report for year:", year);
      
      const data = await reportsAPI.getMonthlyStats(year);
      
      console.log("Monthly report data received:", data);
      
      // Create CSV content with proper number formatting
      let csv = `Monthly Report for ${year}\n\n`;
      csv += "Month,Year,Income,Expenses,Net,Transaction Count\n";
      
      if (data && Array.isArray(data)) {
        data.forEach(m => {
          csv += `"${m.month || "N/A"}",${m.year || year},${formatNumberForCSV(m.income)},${formatNumberForCSV(m.expenses)},${formatNumberForCSV(m.net)},${m.transactionCount || 0}\n`;
        });
      }
      
      // Download CSV
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `monthly_report_${year}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      
      alert("Monthly report downloaded successfully!");
    } catch (err) {
      const errorMsg = err.response?.data?.message || err.message || "Failed to download report";
      setError(errorMsg);
      console.error("Monthly report error:", err);
      console.error("Error response:", err.response?.data);
      alert("Error: " + errorMsg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h1>📊 Reports & Statements</h1>
        <button onClick={() => navigate("/dashboard")} style={styles.backButton}>
          ← Back to Dashboard
        </button>
      </div>

      {error && <div style={styles.error}>{error}</div>}

      {/* Dashboard Statistics */}
      <div style={styles.card}>
        <h2 style={styles.cardTitle}>📈 Dashboard Statistics</h2>
        {!dashboardStats ? (
          <button onClick={fetchDashboard} style={styles.button} disabled={loading}>
            {loading ? "Loading..." : "Load Statistics"}
          </button>
        ) : (
          <div style={styles.statsGrid}>
            <div style={styles.statItem}>
              <label>Current Balance</label>
              <p style={styles.statValue}>₦{formatNumber(dashboardStats.currentBalance)}</p>
            </div>
            <div style={styles.statItem}>
              <label>Total Income</label>
              <p style={{...styles.statValue, color: "#28a745"}}>₦{formatNumber(dashboardStats.totalIncome)}</p>
            </div>
            <div style={styles.statItem}>
              <label>Total Expenses</label>
              <p style={{...styles.statValue, color: "#dc3545"}}>₦{formatNumber(dashboardStats.totalExpenses)}</p>
            </div>
            <div style={styles.statItem}>
              <label>Monthly Income</label>
              <p style={{...styles.statValue, color: "#28a745"}}>₦{formatNumber(dashboardStats.monthlyIncome)}</p>
            </div>
            <div style={styles.statItem}>
              <label>Monthly Expenses</label>
              <p style={{...styles.statValue, color: "#dc3545"}}>₦{formatNumber(dashboardStats.monthlyExpenses)}</p>
            </div>
            <div style={styles.statItem}>
              <label>Total Transactions</label>
              <p style={styles.statValue}>{dashboardStats.transactionCount || 0}</p>
            </div>
          </div>
        )}
      </div>

      {/* Account Statement Download */}
      <div style={styles.card}>
        <h2 style={styles.cardTitle}>📄 Download Account Statement</h2>
        <p style={styles.cardDesc}>Generate and download your account statement for a specific period</p>
        
        <div style={styles.formGrid}>
          <div style={styles.inputGroup}>
            <label style={styles.label}>Start Date</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              style={styles.input}
              max={new Date().toISOString().split('T')[0]}
            />
          </div>
          
          <div style={styles.inputGroup}>
            <label style={styles.label}>End Date</label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              style={styles.input}
              max={new Date().toISOString().split('T')[0]}
            />
          </div>
        </div>
        
        <button 
          onClick={downloadStatement}
          style={{...styles.button, ...styles.downloadButton}}
          disabled={loading || !startDate || !endDate}
        >
          {loading ? "Generating..." : "📥 Download Statement (CSV)"}
        </button>
      </div>

      {/* Monthly Report Download */}
      <div style={styles.card}>
        <h2 style={styles.cardTitle}>📅 Download Monthly Report</h2>
        <p style={styles.cardDesc}>Get a summary of your income and expenses by month</p>
        
        <div style={styles.inputGroup}>
          <label style={styles.label}>Select Year</label>
          <select
            value={year}
            onChange={(e) => setYear(e.target.value)}
            style={styles.input}
          >
            {[...Array(5)].map((_, i) => {
              const y = new Date().getFullYear() - i;
              return <option key={y} value={y}>{y}</option>;
            })}
          </select>
        </div>
        
        <button 
          onClick={downloadMonthlyReport} 
          style={{...styles.button, ...styles.downloadButton}}
          disabled={loading}
        >
          {loading ? "Generating..." : "📥 Download Monthly Report (CSV)"}
        </button>
      </div>

      {/* Recent Transactions Preview */}
      {dashboardStats?.recentTransactions && Array.isArray(dashboardStats.recentTransactions) && dashboardStats.recentTransactions.length > 0 && (
        <div style={styles.card}>
          <h2 style={styles.cardTitle}>🕒 Recent Transactions</h2>
          <div style={styles.transactionList}>
            {dashboardStats.recentTransactions.map(t => (
              <div key={t.id} style={styles.transactionItem}>
                <div>
                  <strong>{t.type || "N/A"}</strong>
                  <p style={styles.transactionDesc}>{t.description || "No description"}</p>
                  <small>{t.createdAt ? new Date(t.createdAt).toLocaleString() : "N/A"}</small>
                </div>
                <div style={styles.transactionAmount}>
                  <span style={{
                    color: (t.amount || 0) >= 0 ? "#28a745" : "#dc3545",
                    fontWeight: "bold"
                  }}>
                    {(t.amount || 0) >= 0 ? "+" : ""}₦{formatNumber(Math.abs(t.amount || 0))}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  container: {
    maxWidth: "1000px",
    margin: "0 auto",
    padding: "20px",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "30px",
  },
  backButton: {
    padding: "10px 20px",
    fontSize: "14px",
    fontWeight: "600",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer",
    backgroundColor: "#6c757d",
    color: "white",
  },
  error: {
    backgroundColor: "#fee",
    color: "#c33",
    padding: "15px",
    borderRadius: "6px",
    marginBottom: "20px",
    border: "1px solid #fcc",
  },
  card: {
    backgroundColor: "white",
    padding: "30px",
    borderRadius: "8px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
    marginBottom: "20px",
  },
  cardTitle: {
    fontSize: "22px",
    marginBottom: "10px",
    color: "#333",
  },
  cardDesc: {
    color: "#666",
    marginBottom: "20px",
  },
  statsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(3, 1fr)",
    gap: "20px",
  },
  statItem: {
    padding: "15px",
    backgroundColor: "#f8f9fa",
    borderRadius: "6px",
  },
  statValue: {
    fontSize: "24px",
    fontWeight: "bold",
    color: "#007bff",
    margin: "10px 0 0 0",
  },
  formGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, 1fr)",
    gap: "20px",
    marginBottom: "20px",
  },
  inputGroup: {
    marginBottom: "20px",
  },
  label: {
    display: "block",
    marginBottom: "8px",
    fontWeight: "500",
    color: "#333",
  },
  input: {
    width: "100%",
    padding: "12px",
    fontSize: "14px",
    border: "1px solid #ddd",
    borderRadius: "4px",
    boxSizing: "border-box",
  },
  button: {
    padding: "12px 24px",
    fontSize: "16px",
    fontWeight: "600",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer",
    backgroundColor: "#007bff",
    color: "white",
  },
  downloadButton: {
    backgroundColor: "#28a745",
    width: "100%",
  },
  transactionList: {
    display: "flex",
    flexDirection: "column",
    gap: "15px",
  },
  transactionItem: {
    display: "flex",
    justifyContent: "space-between",
    padding: "15px",
    backgroundColor: "#f8f9fa",
    borderRadius: "6px",
  },
  transactionDesc: {
    fontSize: "14px",
    color: "#666",
    margin: "5px 0",
  },
  transactionAmount: {
    textAlign: "right",
    fontSize: "18px",
  },
};

